import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import {
  Download, Users, Briefcase, TrendingUp, Target, Filter, Clock, DollarSign, 
  Calendar, Award, UserCheck
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import api from '../lib/api';
import { toast } from 'sonner';

export default function Reports() {
  const [activeTab, setActiveTab] = useState('jobs');
  const [loading, setLoading] = useState(true);
  const [jobData, setJobData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [billingData, setBillingData] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedCostCenter, setSelectedCostCenter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  // Options for filters
  const [customers, setCustomers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [custRes, jobsRes, empRes] = await Promise.all([
        api.get('/customers?page_size=1000'),
        api.get('/jobs/all'),
        api.get('/employees?page_size=1000')
      ]);
      
      setCustomers(custRes.data.items || []);
      setEmployees(empRes.data.items || empRes.data || []);
      
      // Extract unique cost centers from jobs
      const ccs = [...new Set(jobsRes.data.filter(j => j.cost_center).map(j => j.cost_center))];
      setCostCenters(ccs);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString()
      });
      
      if (selectedCustomer !== 'all') params.append('customer_id', selectedCustomer);
      if (selectedCostCenter !== 'all') params.append('cost_center', selectedCostCenter);
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      
      if (activeTab === 'jobs') {
        const jobRes = await api.get(`/reports/job-analytics?${params}`);
        setJobData(jobRes.data);
      } else if (activeTab === 'employees') {
        const empRes = await api.get(`/reports/employee-performance?${params}`);
        setEmployeeData(empRes.data);
      } else if (activeTab === 'payroll') {
        const payRes = await api.get(`/reports/payroll-summary?${params}`);
        setPayrollData(payRes.data);
      } else if (activeTab === 'billing') {
        const billingRes = await api.get(`/reports/billing-report?${params}`);
        setBillingData(billingRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [activeTab, startDate, endDate, selectedCustomer, selectedCostCenter, selectedEmployee]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const exportToCSV = (rows, filename) => {
    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleExport = async (reportType) => {
    try {
      const params = new URLSearchParams({
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString()
      });
      const res = await api.get(`/reports/export/${reportType}?${params}`);
      exportToCSV(res.data.rows, res.data.filename);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
          <p className="font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={entry.dataKey || entry.name || `tt-${index}`} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6', '#f97316', '#06b6d4', '#ec4899'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Advanced analytics and performance metrics
          </p>
        </div>
      </div>

      {/* Report Type Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="jobs" className="flex items-center gap-2" data-testid="jobs-tab">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Job Analytics</span>
            <span className="sm:hidden">Jobs</span>
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2" data-testid="employees-tab">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Employee Performance</span>
            <span className="sm:hidden">Employees</span>
          </TabsTrigger>
          <TabsTrigger value="payroll" className="flex items-center gap-2" data-testid="payroll-tab">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">Payroll Summary</span>
            <span className="sm:hidden">Payroll</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2" data-testid="billing-tab">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Billing Report</span>
            <span className="sm:hidden">Billing</span>
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-xs">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-xs">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date"
                />
              </div>
              {activeTab === 'jobs' && (
                <div className="space-y-2">
                  <Label className="text-xs">Customer</Label>
                  <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                    <SelectTrigger data-testid="customer-filter">
                      <SelectValue placeholder="All Customers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customers.map((cust) => (
                        <SelectItem key={cust.id} value={cust.id}>{cust.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs">Division</Label>
                <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                  <SelectTrigger data-testid="cost-center-filter">
                    <SelectValue placeholder="All Divisions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Divisions</SelectItem>
                    {costCenters.map((cc) => (
                      <SelectItem key={cc} value={cc}>{cc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(activeTab === 'employees' || activeTab === 'payroll') && (
                <div className="space-y-2">
                  <Label className="text-xs">Employee</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger data-testid="employee-filter">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Job Analytics Tab */}
        <TabsContent value="jobs" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Jobs</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : jobData?.summary?.total_jobs || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600">
                    <Target className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Completed</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : jobData?.summary?.completed_jobs || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Completion Rate</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-10" /> : `${jobData?.summary?.completion_rate || 0}%`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Customers</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : jobData?.summary?.unique_customers || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Jobs by Status */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Jobs by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px] w-full" />
                ) : !jobData?.by_status?.length ? (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={jobData.by_status}
                        cx="50%"
                        cy="45%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                        nameKey="name"
                      >
                        {jobData.by_status.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Jobs by Customer */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Top Customers by Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px] w-full" />
                ) : !jobData?.by_customer?.length ? (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={jobData.by_customer.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="customer_name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="job_count" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Jobs" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Job Trend Chart */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Job Trend
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => handleExport('job-analytics')} data-testid="export-jobs-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[250px] w-full" />
              ) : !jobData?.trend?.length ? (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={jobData.trend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Total Jobs" dot={{ fill: '#3b82f6', r: 3 }} />
                    <Line type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} name="Completed" dot={{ fill: '#22c55e', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Cost Center Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Jobs by Division</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[150px] w-full" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium">Division</th>
                        <th className="text-right py-3 px-2 font-medium">Job Count</th>
                        <th className="text-right py-3 px-2 font-medium">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobData?.by_cost_center?.map((cc) => (
                        <tr key={cc.cost_center} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-2 font-medium">{cc.cost_center}</td>
                          <td className="py-3 px-2 text-right">{cc.job_count}</td>
                          <td className="py-3 px-2 text-right">
                            {jobData?.summary?.total_jobs 
                              ? `${((cc.job_count / jobData.summary.total_jobs) * 100).toFixed(1)}%`
                              : '0%'
                            }
                          </td>
                        </tr>
                      ))}
                      {!jobData?.by_cost_center?.length && (
                        <tr>
                          <td colSpan={3} className="py-8 text-center text-muted-foreground">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employee Performance Tab */}
        <TabsContent value="employees" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Employees</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : employeeData?.summary?.total_employees || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Hours</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-12" /> : employeeData?.summary?.total_hours || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Jobs Assigned</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : employeeData?.summary?.total_jobs_assigned || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                    <Target className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Jobs Completed</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : employeeData?.summary?.total_jobs_completed || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Hours/Emp</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-10" /> : employeeData?.summary?.avg_hours_per_employee || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top by Hours */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Top Performers by Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px] w-full" />
                ) : !employeeData?.top_by_hours?.length ? (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={employeeData.top_by_hours} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="hours" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Top by Jobs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-green-500" />
                  Top Performers by Jobs Completed
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px] w-full" />
                ) : !employeeData?.top_by_jobs?.length ? (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={employeeData.top_by_jobs} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="jobs" fill="#22c55e" radius={[0, 4, 4, 0]} name="Jobs" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Hours by Cost Center */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Hours by Division</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : !employeeData?.by_cost_center?.length ? (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={employeeData.by_cost_center}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="cost_center" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="hours" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Hours" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Employee Details Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Employee Details</CardTitle>
              <Button variant="outline" size="sm" onClick={() => handleExport('employee-performance')} data-testid="export-employees-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[300px] w-full" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium">Employee</th>
                        <th className="text-left py-3 px-2 font-medium hidden md:table-cell">Division</th>
                        <th className="text-right py-3 px-2 font-medium">Total Hrs</th>
                        <th className="text-right py-3 px-2 font-medium hidden lg:table-cell">Billable</th>
                        <th className="text-right py-3 px-2 font-medium hidden lg:table-cell">Non-Bill</th>
                        <th className="text-right py-3 px-2 font-medium">Jobs</th>
                        <th className="text-right py-3 px-2 font-medium hidden md:table-cell">Completed</th>
                        <th className="text-right py-3 px-2 font-medium">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeData?.employees?.slice(0, 20).map((emp) => (
                        <tr key={emp.employee_id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium">{emp.employee_name}</p>
                              <p className="text-xs text-muted-foreground md:hidden">{emp.cost_center}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2 hidden md:table-cell">{emp.cost_center}</td>
                          <td className="py-3 px-2 text-right font-medium">{emp.total_hours}</td>
                          <td className="py-3 px-2 text-right hidden lg:table-cell text-green-600">{emp.billable_hours}</td>
                          <td className="py-3 px-2 text-right hidden lg:table-cell text-muted-foreground">{emp.non_billable_hours}</td>
                          <td className="py-3 px-2 text-right">{emp.jobs_assigned}</td>
                          <td className="py-3 px-2 text-right hidden md:table-cell">{emp.jobs_completed}</td>
                          <td className="py-3 px-2 text-right">
                            <Badge variant={emp.completion_rate >= 80 ? "default" : emp.completion_rate >= 50 ? "secondary" : "outline"}>
                              {emp.completion_rate}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {!employeeData?.employees?.length && (
                        <tr>
                          <td colSpan={8} className="py-8 text-center text-muted-foreground">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Summary Tab */}
        <TabsContent value="payroll" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">With Hours</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-8" /> : payrollData?.summary?.total_employees || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Hours</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-12" /> : payrollData?.summary?.total_hours || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Regular</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-12" /> : payrollData?.summary?.total_regular_hours || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Overtime</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-10" /> : payrollData?.summary?.total_overtime_hours || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Billable</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-12" /> : payrollData?.summary?.total_billable_hours || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gray-100 text-gray-600">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Non-Billable</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-12" /> : payrollData?.summary?.total_non_billable_hours || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 text-red-600">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">OT %</p>
                    <p className="text-xl font-bold">
                      {loading ? <Skeleton className="h-6 w-10" /> : `${payrollData?.summary?.overtime_percentage || 0}%`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Hours Breakdown Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Hours Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Regular', value: payrollData?.summary?.total_regular_hours || 0, color: '#22c55e' },
                          { name: 'Overtime', value: payrollData?.summary?.total_overtime_hours || 0, color: '#f97316' }
                        ]}
                        cx="50%"
                        cy="45%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                      >
                        <Cell fill="#22c55e" />
                        <Cell fill="#f97316" />
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Hours by Division</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px] w-full" />
                ) : !payrollData?.by_cost_center?.length ? (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={payrollData.by_cost_center}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="cost_center" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="regular_hours" fill="#22c55e" name="Regular" stackId="a" />
                      <Bar dataKey="overtime_hours" fill="#f97316" name="Overtime" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payroll Details Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Payroll Details
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => handleExport('payroll-summary')} data-testid="export-payroll-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[300px] w-full" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium">Employee</th>
                        <th className="text-left py-3 px-2 font-medium hidden md:table-cell">Division</th>
                        <th className="text-right py-3 px-2 font-medium hidden sm:table-cell">Days</th>
                        <th className="text-right py-3 px-2 font-medium">Total</th>
                        <th className="text-right py-3 px-2 font-medium">Regular</th>
                        <th className="text-right py-3 px-2 font-medium">OT</th>
                        <th className="text-right py-3 px-2 font-medium hidden lg:table-cell">Billable</th>
                        <th className="text-right py-3 px-2 font-medium hidden lg:table-cell">Non-Bill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollData?.employees?.map((emp) => (
                        <tr key={emp.employee_id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium">{emp.employee_name}</p>
                              <p className="text-xs text-muted-foreground md:hidden">{emp.cost_center}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2 hidden md:table-cell">{emp.cost_center}</td>
                          <td className="py-3 px-2 text-right hidden sm:table-cell">{emp.days_worked}</td>
                          <td className="py-3 px-2 text-right font-bold">{emp.total_hours}</td>
                          <td className="py-3 px-2 text-right text-green-600">{emp.regular_hours}</td>
                          <td className="py-3 px-2 text-right">
                            {emp.overtime_hours > 0 ? (
                              <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                                {emp.overtime_hours}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-right hidden lg:table-cell text-emerald-600">{emp.billable_hours}</td>
                          <td className="py-3 px-2 text-right hidden lg:table-cell text-muted-foreground">{emp.non_billable_hours}</td>
                        </tr>
                      ))}
                      {!payrollData?.employees?.length && (
                        <tr>
                          <td colSpan={8} className="py-8 text-center text-muted-foreground">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Overtime Settings Note */}
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                <strong>Overtime Calculation:</strong> Hours exceeding {payrollData?.settings?.overtime_daily_threshold || 8} hours per day 
                are counted as overtime. Weekly threshold is {payrollData?.settings?.overtime_weekly_threshold || 40} hours.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Report Tab */}
        <TabsContent value="billing" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Billable Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold text-green-600">
                    {billingData?.summary?.total_billable_hours || 0}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Non-Billable Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold text-yellow-600">
                    {billingData?.summary?.total_non_billable_hours || 0}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold">
                    {billingData?.summary?.total_hours || 0}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Billable %
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold text-blue-600">
                    {billingData?.summary?.billable_percentage || 0}%
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Billable vs Non-Billable Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Billable vs Non-Billable</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px]" />
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={billingData?.pie_chart || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}h`}
                      >
                        {(billingData?.pie_chart || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Daily Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Hours Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[250px]" />
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={billingData?.daily_chart || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="billable" name="Billable" fill="#22c55e" stackId="a" />
                      <Bar dataKey="non_billable" name="Non-Billable" fill="#eab308" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Invoice-Ready: By Customer */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Billing by Customer (Invoice-Ready)</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('billing')}
                disabled={loading}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px]" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Customer</th>
                        <th className="text-right py-2 px-3">Billable Hours</th>
                        <th className="text-right py-2 px-3">Non-Billable</th>
                        <th className="text-right py-2 px-3">Total Hours</th>
                        <th className="text-right py-2 px-3">Entries</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(billingData?.by_customer || []).map((cust) => (
                        <tr key={cust.customer || cust.id || cust.name} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3 font-medium">{cust.customer}</td>
                          <td className="text-right py-2 px-3 text-green-600">{cust.billable_hours}</td>
                          <td className="text-right py-2 px-3 text-yellow-600">{cust.non_billable_hours}</td>
                          <td className="text-right py-2 px-3 font-medium">{cust.total_hours}</td>
                          <td className="text-right py-2 px-3">{cust.entry_count}</td>
                        </tr>
                      ))}
                      {(!billingData?.by_customer || billingData.by_customer.length === 0) && (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-muted-foreground">
                            No billing data for selected period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Hours by Employee */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hours by Employee</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[200px]" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Employee</th>
                        <th className="text-right py-2 px-3">Billable Hours</th>
                        <th className="text-right py-2 px-3">Non-Billable</th>
                        <th className="text-right py-2 px-3">Total Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(billingData?.by_employee || []).map((emp) => (
                        <tr key={emp.employee || emp.id} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3 font-medium">{emp.employee}</td>
                          <td className="text-right py-2 px-3 text-green-600">{emp.billable_hours}</td>
                          <td className="text-right py-2 px-3 text-yellow-600">{emp.non_billable_hours}</td>
                          <td className="text-right py-2 px-3 font-medium">{emp.total_hours}</td>
                        </tr>
                      ))}
                      {(!billingData?.by_employee || billingData.by_employee.length === 0) && (
                        <tr>
                          <td colSpan={4} className="text-center py-4 text-muted-foreground">
                            No employee data for selected period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
